import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../../assets/images/logos/logo.svg';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(
    () => localStorage.getItem('bannerDismissed') !== 'true'
  );
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const handleNavClick = (e, path) => {
    if (path.includes('#')) {
      e.preventDefault();
      const hash = path.split('#')[1];
      setIsMenuOpen(false);
      if (location.pathname !== '/') {
        navigate(path);
        setTimeout(() => {
          document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else {
        navigate(path);
        setTimeout(() => {
          document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
        }, 300);
      }
    } else if (path === '/' && location.pathname === '/') {
      e.preventDefault();
      setIsMenuOpen(false);
      navigate('/');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'How It Works', path: '/#how-it-works' },
    { name: 'News', path: '/#news' },
  ];

  const isHome = location.pathname === '/';
  const hasDarkHero = location.pathname === '/';

  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      {/* ═══ MODAL ═══ */}
      <AnimatePresence>
        {bannerVisible && (
          <>
            {/* Floating card — bottom right */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 24 }}
              transition={{ duration: 0.4, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="fixed bottom-6 right-6 z-[70] w-[340px] max-w-[calc(100vw-2rem)]"
            >
              <div className="bg-[#0d1a10] border border-brand-800/40 rounded-2xl shadow-2xl shadow-black/40 p-6 relative">
                {/* Close button */}
                <button
                  onClick={() => { setBannerVisible(false); localStorage.setItem('bannerDismissed', 'true'); }}
                  aria-label="Dismiss"
                  className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center text-white/30 hover:text-white/70 transition-colors duration-200"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                {/* Badge */}
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-500/20 border border-brand-500/30 text-brand-400 text-xs font-semibold tracking-widest uppercase mb-5">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
                    <path d="M18 3a1 1 0 0 0-1 1v.083A3 3 0 0 1 16 4H8a3 3 0 0 0-3 3v2a3 3 0 0 0 3 3h.28l-.97 4.26A1 1 0 0 0 8.28 17h2.44a1 1 0 0 0 .97-.757L12.72 13H16a3 3 0 0 1 1 .083V17a1 1 0 0 0 2 0V4a1 1 0 0 0-1-1zM10.28 15H9.5l.8-3.5h1.4l-.8 3.5zM16 11H8a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1z"/>
                  </svg>
                  Closed Beta
                </span>
                <p className="text-white text-base font-medium mb-3 leading-snug">
                  Now accepting applications for our 2026 closed beta - limited spots available.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => { navigate('/contact'); setBannerVisible(false); localStorage.setItem('bannerDismissed', 'true'); }}
                    className="flex-1 py-3 text-sm font-semibold text-white bg-brand-500 hover:bg-brand-600 rounded-full transition-all duration-300 shadow-lg shadow-brand-500/25"
                  >
                    Book a Demo
                  </button>
                  <button
                    onClick={() => { setBannerVisible(false); localStorage.setItem('bannerDismissed', 'true'); }}
                    className="flex-1 py-3 text-sm font-medium text-white/50 hover:text-white/80 border border-white/10 hover:border-white/20 rounded-full transition-all duration-300"
                  >
                    Maybe later
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>


      {/* ═══ NAV HEADER ═══ */}
      <header
        className={`transition-all duration-500 ${
          isScrolled
            ? 'bg-white/80 dark:bg-[#0a0f0d]/80 backdrop-blur-xl shadow-[0_1px_0_0_rgba(0,0,0,0.05)] dark:shadow-[0_1px_0_0_rgba(255,255,255,0.05)]'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center group" onClick={(e) => handleNavClick(e, '/')}>
              <motion.img
                src={logo}
                alt="Budbreak Innovations"
                className="h-10 w-auto transition-transform duration-300 group-hover:scale-105"
                whileHover={{ scale: 1.05 }}
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => {
                const isActive = link.path.includes('#')
                  ? location.hash === '#' + link.path.split('#')[1]
                  : location.pathname === link.path && !location.hash;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={(e) => handleNavClick(e, link.path)}
                    className={`relative px-4 py-2 text-sm font-medium tracking-wide uppercase transition-colors duration-300 rounded-lg ${
                      isActive
                        ? 'text-brand-600 dark:text-brand-400'
                        : isScrolled || !hasDarkHero
                        ? 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
                        : 'text-white/80 hover:text-white'
                    }`}
                  >
                    {link.name}
                    {isActive && (
                      <motion.div
                        layoutId="nav-indicator"
                        className="absolute bottom-0 left-2 right-2 h-0.5 bg-brand-500 rounded-full"
                        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                      />
                    )}
                  </Link>
                );
              })}


              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => navigate('/contact')}
                className="ml-4 px-6 py-2.5 text-sm font-semibold text-white bg-brand-500 hover:bg-brand-600 rounded-full transition-all duration-300 shadow-lg shadow-brand-500/25 hover:shadow-brand-500/35"
              >
                Book a Demo
              </motion.button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`md:hidden relative w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
                isScrolled || !hasDarkHero
                  ? 'text-gray-700 dark:text-gray-300'
                  : 'text-white'
              }`}
              aria-label="Toggle menu"
            >
              <div className="w-5 flex flex-col gap-1.5">
                <span className={`block h-0.5 rounded-full transition-all duration-300 ${
                  isMenuOpen ? 'rotate-45 translate-y-2 bg-gray-900 dark:bg-white' : (isScrolled || !hasDarkHero ? 'bg-gray-700 dark:bg-gray-300' : 'bg-white')
                }`} />
                <span className={`block h-0.5 rounded-full transition-all duration-300 ${
                  isMenuOpen ? 'opacity-0' : (isScrolled || !hasDarkHero ? 'bg-gray-700 dark:bg-gray-300' : 'bg-white')
                }`} />
                <span className={`block h-0.5 rounded-full transition-all duration-300 ${
                  isMenuOpen ? '-rotate-45 -translate-y-2 bg-gray-900 dark:bg-white' : (isScrolled || !hasDarkHero ? 'bg-gray-700 dark:bg-gray-300' : 'bg-white')
                }`} />
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
              className="md:hidden overflow-hidden bg-white/95 dark:bg-[#0a0f0d]/95 backdrop-blur-xl border-t border-gray-200/50 dark:border-white/5"
            >
              <div className="px-6 py-6 space-y-2">
                {navLinks.map((link, i) => (
                  <motion.div
                    key={link.path}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <Link
                      to={link.path}
                      className={`block px-4 py-3 text-lg font-medium rounded-xl transition-all duration-200 ${
                        (link.path.includes('#') ? location.hash === '#' + link.path.split('#')[1] : location.pathname === link.path && !location.hash)
                          ? 'text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-950/50'
                          : 'text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-white/5'
                      }`}
                      onClick={(e) => { handleNavClick(e, link.path); setIsMenuOpen(false); }}
                    >
                      {link.name}
                    </Link>
                  </motion.div>
                ))}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: (navLinks.length + 1) * 0.05 }}
                  className="pt-2"
                >
                  <button
                    onClick={() => {
                      navigate('/contact');
                      setIsMenuOpen(false);
                    }}
                    className="w-full py-3 text-center text-white bg-brand-500 hover:bg-brand-600 rounded-xl transition-colors font-semibold"
                  >
                    Book a Demo
                  </button>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </div>
  );
};

export default Header;
